/*
  My Mandir, on the website.

  A customer tells us once how big their own murti is — "my Laddu Gopal is
  4 NO, my Mata Rani is 6 inch" — and from then on every product page opens
  already set to the size that fits. Choosing the wrong size is the single
  commonest reason a poshak comes back, so this is the most useful thing the
  shop can remember about someone.

  The rules here are a straight port of the app's src/config/sizeCharts.ts and
  src/shopify/mySize.ts. If either side changes, change both — a customer who
  uses the app and the website must not be told two different sizes.

  Kept in localStorage under the same key and the same shape the app uses on
  the phone. It is a note about a household's murti, not about a person, so it
  needs no account and works for a visitor who never signs in.
*/
(function () {
  'use strict';

  var STORAGE_KEY = 'shriposhak.myMandir';

  // Four sizing systems that are NOT interchangeable. Laddu Gopal sizes are
  // written "4NO"; Mata Rani, Radha Krishna and Khatu Shyam are all written
  // in inches but do not mean the same thing — a Mata Rani 4" fits a 7–8
  // inch idol, a Radha Krishna 4" fits a 4 inch idol. Mixing them is how a
  // customer ends up with a dress that does not fit.
  //
  // The last two read the size straight off the idol's height, which is how
  // the store's own product pages describe them ("Available Sizes: Suitable
  // for 3\" to 9\" Idols" on SP-F09, "7, 9, 12 inches" on SP-I25). See
  // src/config/sizeCharts.ts — change both or the app and the site will
  // tell one customer two different sizes.
  var IDOL_HEIGHT_NOTE =
    'On these sets the size is your idol’s own height — a 5 inch idol takes size 5". ' +
    'Between two sizes, go one size up: a slightly loose poshak drapes well and can be ' +
    'tied to fit, a tight one will not go on at all.';

  function range(from, to) {
    var out = [];
    for (var n = from; n <= to; n++) out.push(String(n));
    return out;
  }

  function idolHeightRows(sizes) {
    return sizes.map(function (size) { return [size, size + '"', size + ' inch']; });
  }

  var CHARTS = {
    'laddu-gopal': {
      title: 'Laddu Gopal Ji',
      note: 'Dress diameter is the width of the round poshak, measured straight across through the centre. Poshak is not made in 3 NO — for a 3.5 inch idol, 4 NO is the one to take.',
      columns: ['Size', 'Idol height', 'Dress diameter'],
      rows: [
        ['0', '0 NO', '1.75"', '4"'],
        ['1', '1 NO', '2.4"', '5"'],
        ['2', '2 NO', '2.8"', '6"'],
        ['4', '4 NO', '4"', '8"'],
        ['5', '5 NO', '5"', '10"'],
        ['6', '6 NO', '6"', '12"'],
        ['7', '7 NO', '7"', '14"']
      ]
    },
    'mata-rani': {
      title: 'Mata Rani',
      sellsSizes: ['2', '3', '4', '5', '6', '7', '9', '12', '15'],
      note: 'Mata Rani poshak is made from 2 inch up to 15 inch. The measurements above cover 2 to 9 \u2014 for a larger size, send us your idol\u2019s height on WhatsApp and we will confirm the fit.',
      columns: ['Size', 'Idol height', 'Patka', 'Lehenga'],
      rows: [
        ['2', '2"', '3–4"', '5"', '2"'],
        ['3', '3"', '5–6"', '7"', '3"'],
        ['4', '4"', '7–8"', '10"', '4"'],
        ['5', '5"', '8–9"', '12"', '5"'],
        ['6', '6"', '9–10"', '15"', '6"'],
        ['7', '7"', '11–12"', '18"', '7"'],
        ['9', '9"', '14–15"', '23"', '9"']
      ]
    },
    'radha-krishna': {
      title: 'Radha Krishna',
      note: IDOL_HEIGHT_NOTE,
      columns: ['Size', 'Idol height'],
      rows: idolHeightRows(['2', '3', '4', '5', '6', '7', '9', '12', '15'])
    },
    'khatu-shyam': {
      title: 'Khatu Shyam Ji',
      note: IDOL_HEIGHT_NOTE,
      columns: ['Size', 'Idol height'],
      rows: idolHeightRows(range(3, 6))
    }
  };

  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function write(sizes) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sizes));
    } catch (e) {}
    document.dispatchEvent(new CustomEvent('sp:mandir-changed'));
  }

  function sizeFor(kind) {
    var found = read().filter(function (entry) { return entry.kind === kind; })[0];
    return found ? found.size : null;
  }

  function setSize(kind, size) {
    var sizes = read().filter(function (entry) { return entry.kind !== kind; });
    if (size) sizes.push({ kind: kind, size: size });
    write(sizes);
  }

  // The size numbers inside one option value — "4NO" gives ["4"], "0NO/1NO"
  // gives ["0","1"], '5"' gives ["5"]. "4N0" with a zero is a typo on a few
  // real products and reads the same as "4NO"; "ONO" with a letter O is how
  // size 0 is actually spelled across much of this catalogue, and without
  // that first replacement it holds no digit at all and size 0 would never
  // match anything.
  function sizeNumbersIn(value) {
    var normalised = String(value).replace(/^\s*O(?=\s*N[O0])/i, '0');
    return normalised.match(/\d+/g) || [];
  }

  function sizeValueMatches(value, sizeNumber) {
    return sizeNumbersIn(value).indexOf(sizeNumber) !== -1;
  }

  // Sized in inches, but not poshak — a wall frame is sold "4*6", a toy by
  // its own height. Neither takes a dress size, so neither gets a guide.
  var NOT_A_POSHAK = /\b(toy|frame|photo|poster|mala|counter|book)\b/i;

  // Which chart, if any, fits this product. Mirrors sizeChartFor() in
  // src/config/sizeCharts.ts.
  function chartKindFor(title, sizeValues) {
    if (!sizeValues || !sizeValues.length) return null;

    var looksLaddooGopal = sizeValues.some(function (v) {
      return /^\s*(?:\d+|O)\s*N[O0]/i.test(v);
    });
    if (looksLaddooGopal) return 'laddu-gopal';

    // Plain inches — 4", 5". A frame's "4*6" is not that, and falls out here.
    var inchSized = sizeValues.some(function (v) { return /^\s*\d+(?:\.\d+)?\s*"/.test(v); });
    if (!inchSized || NOT_A_POSHAK.test(title)) return null;

    // Khatu Shyam first: a few of those dresses carry copied Laddu Gopal
    // wording with "Krishna" in it, and the name on the box is the one that
    // says whose idol it is for.
    if (/khatu|shyam/i.test(title)) return 'khatu-shyam';
    if (/radha|krishna|krishn|yugal|jugal/i.test(title)) return 'radha-krishna';
    if (/mata|devi|durga|lehenga|lehnga|patka/i.test(title)) return 'mata-rani';

    return null;
  }

  function prettySizeLabel(value) {
    var numbered = String(value).trim().match(/^(\d+|O)\s*N[O0]$/i);
    if (numbered) return (numbered[1].toUpperCase() === 'O' ? '0' : numbered[1]) + ' NO';
    return String(value).trim();
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  // The chart, drawn the way the app draws it (src/components/SizeGuide.tsx):
  // how to measure first, then the table with the customer's own size picked
  // out, then the one line of small print, then a way to ask a person.
  //
  // The highlight is the whole point of it. A customer is not reading a table
  // — they are answering one question, "is 4 NO right for my 4 inch Laddu
  // Gopal", and the answer should be the row that is already lit up.
  function chartHtml(kind, selectedSize) {
    var chart = CHARTS[kind];
    if (!chart) return '';

    var head = '<tr>' + chart.columns.map(function (c) {
      return '<th>' + escapeHtml(c) + '</th>';
    }).join('') + '</tr>';

    var body = chart.rows.map(function (row) {
      var mine = selectedSize && sizeValueMatches(selectedSize, row[0]);
      return '<tr' + (mine ? ' class="sp-size__row--mine"' : '') + '>' +
        row.slice(1).map(function (cell) { return '<td>' + escapeHtml(cell) + '</td>'; }).join('') +
      '</tr>';
    }).join('');

    var anyMine = selectedSize && chart.rows.some(function (row) {
      return sizeValueMatches(selectedSize, row[0]);
    });

    return '<div class="sp-size__how">' +
        'Measure your idol’s height in inches, then find the closest idol height below — ' +
        'that row is the right size.' +
      '</div>' +
      '<div class="sp-size__scroll"><table class="sp-size__table">' +
        '<thead>' + head + '</thead><tbody>' + body + '</tbody>' +
      '</table></div>' +
      (chart.note ? '<p class="sp-size__note">' + escapeHtml(chart.note) + '</p>' : '') +
      (anyMine ? '<p class="sp-size__mine">Highlighted: your size</p>' : '') +
      '<a class="sp-size__whatsapp" target="_blank" rel="noopener" href="https://wa.me/915653554030?text=' +
        encodeURIComponent('Hi Shri Poshak, please help me choose the right size. My idol’s height is: ') +
        '">Not sure? Send us a photo on WhatsApp</a>';
  }

  // One sheet, built once and reused, so opening it a second time costs
  // nothing and there is never more than one of it in the page.
  var sheet = null;

  function openSizeGuide(kind, selectedSize) {
    var chart = CHARTS[kind];
    if (!chart) return;

    if (!sheet) {
      sheet = document.createElement('div');
      sheet.className = 'sp-size';
      sheet.hidden = true;
      sheet.innerHTML =
        '<div class="sp-size__backdrop" data-close></div>' +
        '<div class="sp-size__panel" role="dialog" aria-modal="true" aria-label="Size guide">' +
          '<div class="sp-size__head">' +
            '<h2 class="sp-size__title" data-title></h2>' +
            '<button class="sp-size__close" type="button" data-close aria-label="Close">&times;</button>' +
          '</div>' +
          '<div class="sp-size__body" data-body></div>' +
        '</div>';
      document.body.appendChild(sheet);
      sheet.addEventListener('click', function (event) {
        if (event.target.hasAttribute('data-close')) closeSizeGuide();
      });
      // Escape closes it, the way every other sheet on the web does.
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closeSizeGuide();
      });
    }

    sheet.querySelector('[data-title]').textContent = chart.title;
    sheet.querySelector('[data-body]').innerHTML = chartHtml(kind, selectedSize);
    sheet.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  function closeSizeGuide() {
    if (!sheet || sheet.hidden) return;
    sheet.hidden = true;
    document.body.style.overflow = '';
  }

  window.SPMandir = {
    CHARTS: CHARTS,
    sizesSoldFor: function (kind) {
      var chart = CHARTS[kind];
      if (!chart) return [];
      return chart.sellsSizes || chart.rows.map(function (row) { return row[0]; });
    },
    labelForSize: function (kind, size) {
      var chart = CHARTS[kind];
      var row = chart && chart.rows.filter(function (r) { return r[0] === size; })[0];
      return row ? row[1] : size + '"';
    },
    chartHtml: chartHtml,
    openSizeGuide: openSizeGuide,
    read: read,
    sizeFor: sizeFor,
    setSize: setSize,
    chartKindFor: chartKindFor,
    sizeValueMatches: sizeValueMatches,
    prettySizeLabel: prettySizeLabel
  };

  // On a product page, pick the customer's size for them.
  //
  // Only the size is chosen. Colour and style are taste, and guessing at
  // someone's taste is how a shop annoys them; the size is a fact about their
  // murti that they already told us.
  function selectMySizeOnProductPage() {
    var form = document.querySelector('variant-selects, variant-radios');
    if (!form) return;

    var title = (document.querySelector('.product__title h1') || {}).textContent || '';

    // Shopify renders options either as radio buttons or as a dropdown,
    // depending on a theme setting, so both have to be handled.
    var groups = form.querySelectorAll('fieldset.js, .product-form__input');

    Array.prototype.forEach.call(groups, function (group) {
      var legend = group.querySelector('legend, .form__label');
      var name = legend ? legend.textContent : '';
      if (!/size/i.test(name)) return;

      var radios = group.querySelectorAll('input[type="radio"]');
      var select = group.querySelector('select');

      var values = [];
      if (radios.length) {
        Array.prototype.forEach.call(radios, function (r) { values.push(r.value); });
      } else if (select) {
        Array.prototype.forEach.call(select.options, function (o) { values.push(o.value); });
      }
      if (!values.length) return;

      var kind = chartKindFor(title, values);
      if (!kind) return;

      var mine = sizeFor(kind);

      // Added before anything else, because the people who need a size chart
      // are exactly the people who have not saved a size. The theme has no
      // size guide of its own, so without this the only chart on the website
      // was on the home page, three taps from the poshak being chosen.
      if (legend && !group.querySelector('[data-sp-size-guide]')) {
        var link = document.createElement('button');
        link.type = 'button';
        link.className = 'sp-size-guide-link';
        link.setAttribute('data-sp-size-guide', kind);
        link.textContent = 'Size guide';
        link.addEventListener('click', function () {
          openSizeGuide(kind, sizeFor(kind));
        });
        legend.appendChild(link);
      }

      if (!mine) return;

      var wanted = values.filter(function (v) { return sizeValueMatches(v, mine); })[0];
      if (!wanted) return;

      if (radios.length) {
        var radio = Array.prototype.filter.call(radios, function (r) { return r.value === wanted; })[0];
        // Already on it, or sold out in that size — leave it alone rather
        // than fighting the theme over a choice that cannot be made.
        if (!radio || radio.checked || radio.disabled) return;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (select) {
        if (select.value === wanted) return;
        select.value = wanted;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function start() {
    selectMySizeOnProductPage();
    // Re-apply if the customer saves a size while standing on a product page.
    document.addEventListener('sp:mandir-changed', selectMySizeOnProductPage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
